import React, { useEffect, useRef, useState, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  APP_LANGUAGES,
  languageFromPath,
  normalizeLanguage,
  withLanguagePrefix,
} from '../i18n/languages';

// Drapeaux servis depuis les assets du projet (pas de CDN) : ils s'affichent
// même hors ligne et ne dépendent pas d'un tiers.
import flagFr from '../assets/fr.svg';
import flagUs from '../assets/us.svg';
import flagEs from '../assets/es.svg';
import flagIt from '../assets/it.svg';
import flagDe from '../assets/de.svg';
import flagPt from '../assets/pt.svg';

const FLAGS: Record<string, string> = {
  fr: flagFr,
  us: flagUs,
  es: flagEs,
  it: flagIt,
  de: flagDe,
  pt: flagPt,
};

/**
 * Sélecteur de langue de la landing page.
 *
 * Même mécanique et même rendu que le `MultiSelect` de /settings?tab=parametres
 * (chevron qui pivote, dropdown en portail, fermeture au clic extérieur ou au
 * scroll, coche sur l'option active) — mais compact : seul le drapeau est
 * visible une fois replié, le libellé n'apparaît que dans la liste.
 *
 * Changer de langue réécrit aussi le préfixe de l'URL (`/fr` → `/es`) quand on se
 * trouve sur une route préfixée, pour que le lien reste partageable.
 */

const ChevronDown = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24">
    <path fill="currentColor" fillRule="evenodd" d="M4.43 8.512a.75.75 0 0 1 1.058-.081L12 14.012l6.512-5.581a.75.75 0 0 1 .976 1.138l-7 6a.75.75 0 0 1-.976 0l-7-6a.75.75 0 0 1-.081-1.057" clipRule="evenodd"/>
  </svg>
);

const Check = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24">
    <path fill="currentColor" d="M16.03 10.03a.75.75 0 1 0-1.06-1.06l-4.47 4.47l-1.47-1.47a.75.75 0 0 0-1.06 1.06l2 2a.75.75 0 0 0 1.06 0z"/>
  </svg>
);

const Flag = ({ code, alt, className = '' }: { code: string; alt: string; className?: string }) => (
  <img
    src={FLAGS[code]}
    alt={alt}
    width={22}
    height={16}
    className={`w-[22px] h-4 object-cover shrink-0 ${className}`}
    style={{ borderRadius: '3px' }}
  />
);

interface LanguageFlagSelectorProps {
  /** Palette sombre (navbar de la landing) ou claire. */
  isDark?: boolean;
  className?: string;
}

export const LanguageFlagSelector: React.FC<LanguageFlagSelectorProps> = ({ isDark = true, className = '' }) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLSpanElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const current = normalizeLanguage(i18n.language) ?? 'fr';
  const currentLanguage = APP_LANGUAGES.find(l => l.code === current) ?? APP_LANGUAGES[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ferme le dropdown si on scrolle dans un parent (mais pas à l'intérieur du dropdown)
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const calculateDropdownPosition = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const margin = 6;
    const width = 190;
    const fullContentHeight = APP_LANGUAGES.length * 44 + 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openUpward = spaceBelow < fullContentHeight && spaceAbove > spaceBelow;

    const style: CSSProperties = {
      position: 'fixed',
      // Aligné à droite du déclencheur : le sélecteur vit en bout de navbar,
      // un alignement à gauche déborderait de l'écran sur petits viewports.
      left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
      width,
      maxHeight: Math.max(Math.min(fullContentHeight, openUpward ? spaceAbove : spaceBelow), 120),
      zIndex: 9999,
      overflow: 'hidden',
    };
    if (openUpward) {
      style.bottom = window.innerHeight - rect.top + margin;
    } else {
      style.top = rect.bottom + margin;
    }
    setDropdownStyle(style);
  };

  const handleSelect = (code: string) => {
    setIsOpen(false);
    if (code === current) return;
    i18n.changeLanguage(code);
    // Sur une route préfixée (`/fr`, `/es`…), l'URL doit suivre la langue.
    if (languageFromPath(location.pathname)) {
      navigate(withLanguagePrefix(location.pathname, code) + location.search + location.hash, { replace: true });
    }
  };

  const borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgb(226,232,240)';
  const triggerColor = isDark ? '#ffffff' : '#0f172a';
  const dropBg = isDark ? '#16181C' : '#ffffff';
  const dropText = isDark ? '#ffffff' : '#0f172a';
  const dropHover = isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc';
  const activeBg = isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9';

  return (
    // <span> et non <div> : dans la barre mobile de la navbar, `index.css` peint
    // en blanc tout enfant direct `div` de `.lg:hidden` (règles legacy du header
    // mobile de l'app, en !important). Un span passe à côté de ces sélecteurs.
    <span ref={containerRef} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        aria-label={t('landing.common.language_placeholder')}
        aria-expanded={isOpen}
        onClick={() => {
          if (!isOpen) calculateDropdownPosition();
          setIsOpen(!isOpen);
        }}
        className="inline-flex items-center gap-1 rounded-[10px] px-1.5 py-1.5 transition-colors"
        style={{ border: 'none', color: triggerColor, background: 'transparent', cursor: 'pointer' }}
      >
        <Flag code={currentLanguage.flagCode} alt={currentLanguage.label} />
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 opacity-60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{ ...dropdownStyle, background: dropBg, border: `1px solid ${borderColor}` }}
          className="rounded-2xl shadow-lg overflow-hidden"
        >
          <div className="py-1 custom-scrollbar" style={{ maxHeight: 'inherit', overflowY: 'auto' }}>
            {APP_LANGUAGES.map(language => {
              const isActive = language.code === current;
              return (
                <button
                  key={language.code}
                  type="button"
                  onClick={() => handleSelect(language.code)}
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-sm transition-colors"
                  style={{ color: dropText, background: isActive ? activeBg : 'transparent', border: 'none', cursor: 'pointer' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = dropHover; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <Flag code={language.flagCode} alt="" />
                  <span className="flex-1 text-left whitespace-nowrap">{language.label}</span>
                  {isActive && <Check className="h-4 w-4 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
};

export default LanguageFlagSelector;
